import {
  Box,
  Button,
  Center,
  Circle,
  Divider,
  Flex,
  Image,
  Link,
  Text,
} from '@chakra-ui/react';
import { getContrastYIQ } from '../../../../../utils/colorBasedOnBgContrast';
import { useContext, useEffect, useMemo, useState } from 'react';
import { MeContext } from '../../../../../context/MeContext';
import previewWatermarkLight from '../../../../../assets/vectors/previewWatermarkLight.svg';
import previewWatermarkDark from '../../../../../assets/vectors/previewWatermarkDark.svg';
import { CustomNfcIcon } from '../../../../../theme/icons/CustomNfcIcon';
import { TrialBannerOverlay } from '../shared/TrialBannerOverlay';
import { ImagePlaceholder } from '../shared/ImagePlaceholder';
import { ArrowBackIcon, ArrowForwardIcon } from '@chakra-ui/icons';
import kebabMenuIcon from '../../../../../assets/vectors/kebabMenuIcon.png';
import { CustomLinkIcon } from '../../../../../theme/icons/CustomLinkIcon';
import { PreviewData, TemplateType } from '../../../../../store/types/template';
import { PassNotification } from '../shared/PassNotification';
import {
  getGoogleAppLinkHref,
  getGoogleBackgroundColor,
  getGoogleSecValuesPresent,
  getGoogleAuxValuesPresent,
  getGoogleFirstRowCount,
  getGoogleSecondRowCount,
  getGoogleRowAlignments,
  getBarcodePreviewConfig,
  getGoogleWatermarkConfig,
} from '../../utils/googlePassPreview';
import { getImageDimensions } from '../../utils/imageDimensions';
import { GooglePassField } from './GooglePassField';
import BarcodePreview from '../BarcodePreview';

const GooglePassFront = ({
  previewData,
  createTemplate,
  notifOpen,
  templatesPage = false,
}: {
  previewData: PreviewData;
  createTemplate: boolean;
  notifOpen: boolean;
  templatesPage?: boolean;
}) => {
  const meCtx = useContext(MeContext);
  const watermarkEnabled = (meCtx as any).state.watermarkEnabled;
  const isInTrial = (meCtx as any).state.organisationStatus === 'trial';

  const isWatermarkFieldHighlighted = useMemo(() => {
    return (
      previewData.isFieldHighlighted?.('design.shared.watermarkColor') ||
      previewData.isFieldHighlighted?.('design.google.watermarkColor') ||
      previewData.isFieldHighlighted?.('design.shared.watermarkPosition') ||
      previewData.isFieldHighlighted?.('design.google.watermarkPosition')
    );
  }, [previewData.isFieldHighlighted]);

  const isBarcodeFieldHighlighted = useMemo(() => {
    return (
      previewData.isFieldHighlighted?.('barcode.enabled') ||
      previewData.isFieldHighlighted?.('barcode.type') ||
      previewData.isFieldHighlighted?.('barcode.source') ||
      previewData.isFieldHighlighted?.('barcode.value') ||
      previewData.isFieldHighlighted?.('barcode.displayText')
    );
  }, [previewData.isFieldHighlighted]);

  const [watermarkColor, setWatermarkColor] = useState('');
  const [watermarkPosition, setWatermarkPosition] = useState('');
  const [secValuesPresent, setSecValuesPresent] = useState(true);
  const [auxValuesPresent, setAuxValuesPresent] = useState(true);
  const [firstRowLabelsCount, setFirstRowLabelsCount] = useState(0);
  const [secondRowLabelsCount, setSecondRowLabelsCount] = useState(0);
  const [detailsTwoAlignment, setDetailsTwoAlignment] = useState<
    'left' | 'center' | 'right'
  >('left');
  const [detailsThreeAlignment, setDetailsThreeAlignment] = useState<
    'left' | 'center' | 'right'
  >('left');
  const [detailsFiveAlignment, setDetailsFiveAlignment] = useState<
    'left' | 'center' | 'right'
  >('left');
  const [detailsSixAlignment, setDetailsSixAlignment] = useState<
    'left' | 'center' | 'right'
  >('left');
  const [previewBarcodeImage, setPreviewBarcodeImage] = useState<string | null>(
    null
  );
  const [isBannerHovered, setIsBannerHovered] = useState(false);

  // background + contrast
  const backgroundColor = useMemo(
    () => getGoogleBackgroundColor(previewData),
    [previewData.passDesign?.backgroundColor, previewData.passBackgroundColor]
  );

  const getContrastColor = useMemo(
    () => getContrastYIQ(backgroundColor),
    [backgroundColor]
  );

  // barcode (image + dimensions)
  const {
    image: barcodeImage,
    width: previewBarcodeImageWidth,
    height: previewBarcodeImageHeight,
  } = useMemo(
    () => getBarcodePreviewConfig(previewData),
    [previewData?.barcode?.type]
  );

  const mapAlignToJustify = (a: 'left' | 'center' | 'right') =>
    a === 'left' ? 'flex-start' : a === 'center' ? 'center' : 'flex-end';

  const defaultBannerRatio = useMemo(() => {
    const { width, height } = getImageDimensions(
      previewData.passType as TemplateType
    ).google.banner;
    return String(height === 0 ? 0 : width / height);
  }, [previewData.passType]);

  const bannerSrcForRatio =
    previewData.googleBannerImage || previewData.bannerImage || null;

  const [googleBannerAspectRatio, setGoogleBannerAspectRatio] = useState(defaultBannerRatio);

  useEffect(() => {
    if (!bannerSrcForRatio) {
      setGoogleBannerAspectRatio(defaultBannerRatio);
      return;
    }
    const img = document.createElement('img');
    img.src = bannerSrcForRatio;
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      setGoogleBannerAspectRatio(String(img.naturalWidth / img.naturalHeight));
      return;
    }
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setGoogleBannerAspectRatio(String(img.naturalWidth / img.naturalHeight));
      }
    };
    return () => { img.onload = null; };
  }, [bannerSrcForRatio, defaultBannerRatio]);

  const shouldShowBanner =
    previewData.passType === 'transit'
      ? !!previewData.googleBannerImage
      : previewData.googleBannerImage
        ? previewData.googleBannerImage
        : !!previewData.bannerImage;

  const hasAppLink =
    !!previewData.googleAppLinkPlayStoreUrl ||
    !!previewData.googleAppLinkPackageName ||
    !!previewData.googleAppLinkWebAppUrl;

  useEffect(() => {
    setPreviewBarcodeImage(barcodeImage);
  }, [barcodeImage]);

  // presence of sec/aux values
  useEffect(() => {
    setSecValuesPresent(getGoogleSecValuesPresent(previewData));
  }, [
    previewData.detailsOneId,
    previewData.detailsOneLabel,
    previewData.detailsOneValue,
    previewData.detailsTwoId,
    previewData.detailsTwoLabel,
    previewData.detailsTwoValue,
    previewData.detailsThreeId,
    previewData.detailsThreeLabel,
    previewData.detailsThreeValue,
  ]);

  useEffect(() => {
    setAuxValuesPresent(getGoogleAuxValuesPresent(previewData));
  }, [
    previewData.passType,
    previewData.detailsFourId,
    previewData.detailsFourLabel,
    previewData.detailsFourValue,
    previewData.detailsFiveId,
    previewData.detailsFiveLabel,
    previewData.detailsFiveValue,
    previewData.detailsSixId,
    previewData.detailsSixLabel,
    previewData.detailsSixValue,
  ]);

  // counts
  useEffect(() => {
    setFirstRowLabelsCount(getGoogleFirstRowCount(previewData));
  }, [
    previewData.detailsOneId,
    previewData.detailsOneLabel,
    previewData.detailsOneValue,
    previewData.detailsTwoId,
    previewData.detailsTwoLabel,
    previewData.detailsTwoValue,
    previewData.detailsThreeId,
    previewData.detailsThreeLabel,
    previewData.detailsThreeValue,
  ]);

  useEffect(() => {
    setSecondRowLabelsCount(getGoogleSecondRowCount(previewData));
  }, [
    previewData.detailsFourId,
    previewData.detailsFourLabel,
    previewData.detailsFourValue,
    previewData.detailsFiveId,
    previewData.detailsFiveLabel,
    previewData.detailsFiveValue,
    previewData.detailsSixId,
    previewData.detailsSixLabel,
    previewData.detailsSixValue,
  ]);

  // alignments for sec row
  useEffect(() => {
    const { secondAlignment, thirdAlignment } = getGoogleRowAlignments(
      firstRowLabelsCount,
      [
        previewData.detailsOneId,
        previewData.detailsTwoId,
        previewData.detailsThreeId,
      ],
      [
        previewData.detailsOneLabel,
        previewData.detailsTwoLabel,
        previewData.detailsThreeLabel,
      ],
      [
        previewData.detailsOneValue,
        previewData.detailsTwoValue,
        previewData.detailsThreeValue,
      ]
    );

    setDetailsTwoAlignment(secondAlignment);
    setDetailsThreeAlignment(thirdAlignment);
  }, [
    firstRowLabelsCount,
    previewData.detailsOneId,
    previewData.detailsTwoId,
    previewData.detailsThreeId,
    previewData.detailsOneLabel,
    previewData.detailsTwoLabel,
    previewData.detailsThreeLabel,
    previewData.detailsOneValue,
    previewData.detailsTwoValue,
    previewData.detailsThreeValue,
  ]);

  // alignments for aux row
  useEffect(() => {
    const { secondAlignment, thirdAlignment } = getGoogleRowAlignments(
      secondRowLabelsCount,
      [
        previewData.detailsFourId,
        previewData.detailsFiveId,
        previewData.detailsSixId,
      ],
      [
        previewData.detailsFourLabel,
        previewData.detailsFiveLabel,
        previewData.detailsSixLabel,
      ],
      [
        previewData.detailsFourValue,
        previewData.detailsFiveValue,
        previewData.detailsSixValue,
      ]
    );

    setDetailsFiveAlignment(secondAlignment);
    setDetailsSixAlignment(thirdAlignment);
  }, [
    secondRowLabelsCount,
    previewData.detailsFourId,
    previewData.detailsFiveId,
    previewData.detailsSixId,
    previewData.detailsFourLabel,
    previewData.detailsFiveLabel,
    previewData.detailsSixLabel,
    previewData.detailsFourValue,
    previewData.detailsFiveValue,
    previewData.detailsSixValue,
  ]);

  // watermark config
  useEffect(() => {
    const { color, position } = getGoogleWatermarkConfig(previewData);
    setWatermarkColor(color);
    setWatermarkPosition(position);
  }, [
    previewData.googleBannerImage,
    previewData.googleBannerWatermarkColor,
    previewData.googleBannerWatermarkPosition,
    previewData.watermarkColor,
    previewData.watermarkPosition,
  ]);

  return (
    <>
      {/* PHONE ICONS (ARROW BACK + KEBAB MENU) */}
      {!templatesPage && (
        <Box
          position="absolute"
          inset="13% 8% auto 10%"
          display="flex"
          justifyContent="space-between"
          pr="10px"
          alignContent="center"
          zIndex={3}
        >
          <ArrowBackIcon fontSize="20px" />
          <Image
            src={kebabMenuIcon}
            alt="kebab-menu"
            width="13px"
            height="13px"
          />
        </Box>
      )}

      {/* NOTIFICATION */}
      {!templatesPage && (
        <PassNotification
          open={notifOpen}
          autoHideMs={0}
          title={previewData?.notificationHeader || 'Notification header'}
          subtitle={
            previewData?.notificationMessage ?? 'Your message text here'
          }
          imageUrl={previewData.googleLogoImage || previewData.iconLogo}
          inset="12% 3.5% auto 3%"
          device="google"
        />
      )}

      {/* NFC ICON */}
      {!templatesPage && previewData.googleNfcEnabled && (
        <Box position="absolute" inset="19% 10% auto 41%" zIndex={2}>
          <Circle size="35px" border="2px" p={6}>
            <CustomNfcIcon fontSize="30px" />
          </Circle>
        </Box>
      )}

      {/* PASS CONTENT */}
      <Box
        position={templatesPage ? 'static' : 'absolute'}
        top={
          templatesPage
            ? undefined
            : previewData.googleNfcEnabled
              ? '30%'
              : '18%'
        }
        left={templatesPage ? undefined : '0%'}
        right={templatesPage ? undefined : '3%'}
        bottom={templatesPage ? undefined : hasAppLink ? '16%' : '10%'}
        overflowY={templatesPage ? 'visible' : 'auto'}
        overflowX="hidden"
        sx={{
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
        zIndex={1}
      >
        <Box
          position="relative"
          ml={templatesPage ? 0 : '6%'}
          mr={templatesPage ? 0 : '10%'}
          bg={backgroundColor}
          borderRadius="25px"
          border="1px solid #D3D3D3"
          w={templatesPage ? '100%' : '295px'}
          maxW="full"
        >
          <Flex
            pt="10px"
            pb="5px"
            pl="15px"
            pr="15px"
            alignItems="center"
            justifyContent="flex-start"
          >
            {/* ICON / WIDE LOGO */}
            <Box>
              {(() => {
                const wideLogoImage = previewData.googleWideLogoImage;
                const logoImage =
                  previewData.googleLogoImage || previewData.iconLogo;
                const isIconHighlighted =
                  previewData.isFieldHighlighted?.('design.shared.icon') ||
                  previewData.isFieldHighlighted?.('design.google.icon') ||
                  previewData.isFieldHighlighted?.('design.google.logo') ||
                  previewData.isFieldHighlighted?.('design.google.wideLogo');

                if (wideLogoImage) {
                  return (
                    <Box
                      p="4px"
                      borderRadius="4px"
                      outline={isIconHighlighted ? '2px solid #FF4935' : 'none'}
                    >
                      <Image
                        maxW="100px"
                        maxH="32px"
                        objectFit="contain"
                        src={wideLogoImage}
                        alt=""
                      />
                    </Box>
                  );
                }

                if (logoImage || isIconHighlighted) {
                  return (
                    <Box
                      p="4px"
                      borderRadius="4px"
                      outline={isIconHighlighted ? '2px solid #FF4935' : 'none'}
                    >
                      <Circle size="20px" border="1px" borderColor="white">
                        {logoImage ? (
                          <Image
                            borderRadius="100%"
                            boxSize="full"
                            objectFit="cover"
                            src={logoImage}
                            alt=""
                          />
                        ) : (
                          <ImagePlaceholder
                            borderRadius="100%"
                            boxSize="full"
                          />
                        )}
                      </Circle>
                    </Box>
                  );
                } else {
                  return (
                    <Box p="4px">
                      <ImagePlaceholder
                        height="20px"
                        width="20px"
                        borderRadius="100%"
                      />
                    </Box>
                  );
                }
              })()}
            </Box>

            {/* DESCRIPTION — hidden when wide logo is displayed */}
            {!previewData.googleWideLogoImage && (
              <Box pl="12px">
                <Text
                  fontSize="12px"
                  textStyle="headingFamilyRegularGooglePay"
                  textAlign="center"
                  color={getContrastColor}
                  border={
                    previewData.isFieldHighlighted?.('description')
                      ? '2px solid #FF4935'
                      : 'none'
                  }
                  borderRadius="4px"
                  p={
                    previewData.isFieldHighlighted?.('description') ? '4px' : '0'
                  }
                >
                  {previewData.description
                    ? previewData.description
                    : 'Pass Description'}
                </Text>
              </Box>
            )}
          </Flex>

          {/* DIVIDER */}
          <Center>
            <Divider
              mt={1}
              borderColor={getContrastColor}
              opacity="0.2"
              width="100%"
            />
          </Center>

          {/* CENTRAL TITLE */}
          {(previewData.passType === 'loyalty' ||
            previewData.passType === 'stampCard' ||
            previewData.passType === 'eventTicket') && (
            <Flex width="full" alignItems="flex-start" pt="10px" pl="22px">
              <Text
                fontSize="16px"
                textStyle="headingFamilyRegularGooglePay"
                color={getContrastColor}
                wordBreak="break-word"
                border={
                  previewData.isFieldHighlighted?.('centralTitle')
                    ? '2px solid #FF4935'
                    : 'none'
                }
                borderRadius="4px"
                p={
                  previewData.isFieldHighlighted?.('centralTitle') ? '4px' : '0'
                }
                opacity={previewData.centralTitle ? 1 : 0.4}
              >
                {previewData.centralTitle || 'Pass title'}
              </Text>
            </Flex>
          )}

          {/* HEADER ONE */}
          {(previewData.passType === 'loyalty' ||
            previewData.passType === 'stampCard' ||
            previewData.passType === 'eventTicket') && (
            <Box>
              <Flex
                width="full"
                direction="column"
                alignItems="flex-start"
                pl="22px"
                pt={
                  previewData.centralTitle &&
                  (previewData.passType === 'loyalty' ||
                    previewData.passType === 'stampCard' ||
                    previewData.passType === 'eventTicket')
                    ? '3'
                    : '2'
                }
              >
                <GooglePassField
                  hideIdPlaceholder={previewData.hideIdPlaceholder}
                  label={previewData.headerOneLabel || ''}
                  value={previewData.headerOneValue}
                  id={previewData.headerOneId}
                  color={getContrastColor}
                  labelFontSize="11px"
                  valueFontSize="13px"
                  textAlign="left"
                  wordBreak="break-word"
                  isHighlighted={
                    (previewData.headerOneId ||
                      previewData.headerOneLabel ||
                      previewData.headerOneValue) &&
                    (previewData.isFieldHighlighted?.('header.one.id') ||
                      previewData.isFieldHighlighted?.('header.one.label') ||
                      previewData.isFieldHighlighted?.(
                        'header.one.defaultValue'
                      ))
                  }
                />
              </Flex>
            </Box>
          )}

          {/* PRIMARY ONE (GENERIC) */}
          {previewData.passType === 'generic' && (
            <Flex width="full" alignItems="flex-start" mt={2} pl="22px">
              <GooglePassField
                hideIdPlaceholder
                label={
                  previewData.primaryOneLabel ||
                  (previewData.primaryOneId ? '' : previewData.description)
                }
                value={previewData.primaryOneValue}
                id={previewData.primaryOneId}
                color={getContrastColor}
                labelFontSize="14px"
                valueFontSize="14px"
                textAlign="left"
                wordBreak="break-word"
                isHighlighted={
                  (previewData.primaryOneId ||
                    previewData.primaryOneLabel ||
                    previewData.primaryOneValue) &&
                  (previewData.isFieldHighlighted?.('central.one.id') ||
                    previewData.isFieldHighlighted?.('central.one.label') ||
                    previewData.isFieldHighlighted?.(
                      'central.one.defaultValue'
                    ))
                }
              />
            </Flex>
          )}

          {/* TRANSIT COMPONENTS */}
          {previewData.passType === 'transit' && (
            <>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                mt={2}
                position="relative"
              >
                {/* PRIMARY ONE (TRANSIT) */}
                <Flex
                  width="full"
                  alignItems="flex-start"
                  pl="18px"
                  maxWidth="47%"
                >
                  <GooglePassField
                    hideIdPlaceholder={previewData.hideIdPlaceholder}
                    label={undefined}
                    value={previewData.primaryOneValue}
                    id={previewData.primaryOneId}
                    color={getContrastColor}
                    valueFontSize="16px"
                    mtWhenNoLabel="0"
                    wordBreak="break-word"
                    isHighlighted={
                      (previewData.primaryOneId ||
                        previewData.primaryOneValue) &&
                      (previewData.isFieldHighlighted?.('central.one.id') ||
                        previewData.isFieldHighlighted?.(
                          'central.one.defaultValue'
                        ))
                    }
                  />
                </Flex>

                {/* ARROW ICON */}
                <Box
                  position="absolute"
                  left="50%"
                  top="50%"
                  transform="translate(-50%, -50%)"
                  pointerEvents="none"
                  zIndex={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <ArrowForwardIcon fontSize="15px" color={getContrastColor} />
                </Box>

                {/* PRIMARY TWO */}
                <Flex width="full" pl="12px" pr="10px" maxWidth="47%">
                  <GooglePassField
                    hideIdPlaceholder={previewData.hideIdPlaceholder}
                    label={undefined}
                    value={previewData.primaryTwoValue}
                    id={previewData.primaryTwoId}
                    color={getContrastColor}
                    valueFontSize="16px"
                    mtWhenNoLabel="0"
                    wordBreak="break-word"
                    isHighlighted={
                      (previewData.primaryTwoId ||
                        previewData.primaryTwoValue) &&
                      (previewData.isFieldHighlighted?.('central.two.id') ||
                        previewData.isFieldHighlighted?.(
                          'central.two.defaultValue'
                        ))
                    }
                  />
                </Flex>
              </Flex>
              {/* HEADER ONE */}
              <Box>
                <Flex
                  width="full"
                  direction="column"
                  alignItems="flex-start"
                  pl="22px"
                  pt={3}
                >
                  <GooglePassField
                    hideIdPlaceholder={previewData.hideIdPlaceholder}
                    label={previewData.headerOneLabel || ''}
                    value={previewData.headerOneValue}
                    id={previewData.headerOneId}
                    color={getContrastColor}
                    labelFontSize="11px"
                    valueFontSize="13px"
                    textAlign="left"
                    wordBreak="break-word"
                    isHighlighted={
                      (previewData.headerOneId ||
                        previewData.headerOneLabel ||
                        previewData.headerOneValue) &&
                      (previewData.isFieldHighlighted?.('header.one.id') ||
                        previewData.isFieldHighlighted?.('header.one.label') ||
                        previewData.isFieldHighlighted?.(
                          'header.one.defaultValue'
                        ))
                    }
                  />
                </Flex>
              </Box>
            </>
          )}

          {/* DIVIDER */}
          {secValuesPresent && previewData.passType !== 'generic' && (
            <Center>
              <Divider
                mt={3}
                borderColor={getContrastColor}
                opacity="0.2"
                width="92%"
              />
            </Center>
          )}

          {/* DETAILS FIELDS */}
          <Flex justifyContent="space-between" pl="16px" pr="20px" mt={3}>
            {/* DETAILS ONE */}
            {(!!previewData.detailsOneId ||
              !!previewData.detailsOneLabel ||
              !!previewData.detailsOneValue ||
              !!previewData.isFieldHighlighted?.('details.one.id') ||
              !!previewData.isFieldHighlighted?.('details.one.label') ||
              !!previewData.isFieldHighlighted?.(
                'details.one.defaultValue'
              )) && (
              <Box flex="1" minW="50px" pl={2} maxW="100px" overflow="hidden">
                <GooglePassField
                  hideIdPlaceholder={previewData.hideIdPlaceholder}
                  label={previewData.detailsOneLabel || ''}
                  value={previewData.detailsOneValue}
                  id={previewData.detailsOneId}
                  color={getContrastColor}
                  isHighlighted={
                    !!previewData.isFieldHighlighted?.('details.one.id') ||
                    !!previewData.isFieldHighlighted?.('details.one.label') ||
                    !!previewData.isFieldHighlighted?.(
                      'details.one.defaultValue'
                    )
                  }
                  textAlign="left"
                  mtWhenNoLabel="15px"
                />
              </Box>
            )}
            {/* DETAILS TWO */}
            {(!!previewData.detailsTwoId ||
              !!previewData.detailsTwoLabel ||
              !!previewData.detailsTwoValue ||
              !!previewData.isFieldHighlighted?.('details.two.id') ||
              !!previewData.isFieldHighlighted?.('details.two.label') ||
              !!previewData.isFieldHighlighted?.(
                'details.two.defaultValue'
              )) && (
              <Box
                flex="1"
                minW="50px"
                pl={2}
                maxW="100px"
                overflow="hidden"
                display="flex"
                justifyContent={mapAlignToJustify(detailsTwoAlignment)}
              >
                <GooglePassField
                  hideIdPlaceholder={previewData.hideIdPlaceholder}
                  label={previewData.detailsTwoLabel || ''}
                  value={previewData.detailsTwoValue}
                  id={previewData.detailsTwoId}
                  color={getContrastColor}
                  isHighlighted={
                    !!previewData.isFieldHighlighted?.('details.two.id') ||
                    !!previewData.isFieldHighlighted?.('details.two.label') ||
                    !!previewData.isFieldHighlighted?.(
                      'details.two.defaultValue'
                    )
                  }
                  textAlign={detailsTwoAlignment}
                  mtWhenNoLabel="15px"
                />
              </Box>
            )}

            {/* DETAILS THREE */}
            {(!!previewData.detailsThreeId ||
              !!previewData.detailsThreeLabel ||
              !!previewData.detailsThreeValue ||
              !!previewData.isFieldHighlighted?.('details.three.id') ||
              !!previewData.isFieldHighlighted?.('details.three.label') ||
              !!previewData.isFieldHighlighted?.(
                'details.three.defaultValue'
              )) && (
              <Box
                flex="1"
                minW="50px"
                pl={2}
                maxW="100px"
                overflow="hidden"
                display="flex"
                justifyContent={mapAlignToJustify(detailsThreeAlignment)}
              >
                <GooglePassField
                  hideIdPlaceholder={previewData.hideIdPlaceholder}
                  label={previewData.detailsThreeLabel || ''}
                  value={previewData.detailsThreeValue}
                  id={previewData.detailsThreeId}
                  color={getContrastColor}
                  isHighlighted={
                    !!previewData.isFieldHighlighted?.('details.three.id') ||
                    !!previewData.isFieldHighlighted?.('details.three.label') ||
                    !!previewData.isFieldHighlighted?.(
                      'details.three.defaultValue'
                    )
                  }
                  textAlign={detailsThreeAlignment}
                  mtWhenNoLabel="15px"
                />
              </Box>
            )}
          </Flex>

          {/* 2ND ROW FIELDS */}
          {auxValuesPresent && (
            <>
              {/* DIVIDER */}
              <Center>
                <Divider
                  borderColor={getContrastColor}
                  opacity="0.2"
                  mt={secValuesPresent ? '3' : '0'}
                  width="92%"
                />
              </Center>
              <Flex justifyContent="space-between" pl="16px" pr="20px" mt={3}>
                {/* DETAILS FOUR */}
                {(!!previewData.detailsFourId ||
                  !!previewData.detailsFourLabel ||
                  !!previewData.detailsFourValue ||
                  !!previewData.isFieldHighlighted?.('details.four.id') ||
                  !!previewData.isFieldHighlighted?.('details.four.label') ||
                  !!previewData.isFieldHighlighted?.(
                    'details.four.defaultValue'
                  )) && (
                  <Box
                    flex="1"
                    minW="50px"
                    maxW="100px"
                    overflow="hidden"
                    pl={2}
                  >
                    <GooglePassField
                      hideIdPlaceholder={previewData.hideIdPlaceholder}
                      label={previewData.detailsFourLabel || ''}
                      value={previewData.detailsFourValue}
                      id={previewData.detailsFourId}
                      color={getContrastColor}
                      isHighlighted={
                        !!previewData.isFieldHighlighted?.('details.four.id') ||
                        !!previewData.isFieldHighlighted?.(
                          'details.four.label'
                        ) ||
                        !!previewData.isFieldHighlighted?.(
                          'details.four.defaultValue'
                        )
                      }
                      mtWhenNoLabel="15px"
                    />
                  </Box>
                )}
                {/* DETAILS FIVE */}
                {previewData.passType !== 'loyalty' &&
                  previewData.passType !== 'stampCard' && (
                    <>
                      {(!!previewData.detailsFiveId ||
                        !!previewData.detailsFiveLabel ||
                        !!previewData.detailsFiveValue ||
                        !!previewData.isFieldHighlighted?.('details.five.id') ||
                        !!previewData.isFieldHighlighted?.(
                          'details.five.label'
                        ) ||
                        !!previewData.isFieldHighlighted?.(
                          'details.five.defaultValue'
                        )) && (
                        <Box
                          flex="1"
                          minW="50px"
                          maxW="100px"
                          overflow="hidden"
                          pl={2}
                          display="flex"
                          justifyContent={mapAlignToJustify(
                            detailsFiveAlignment
                          )}
                        >
                          <GooglePassField
                            hideIdPlaceholder={previewData.hideIdPlaceholder}
                            label={previewData.detailsFiveLabel || ''}
                            value={previewData.detailsFiveValue}
                            id={previewData.detailsFiveId}
                            color={getContrastColor}
                            isHighlighted={
                              !!previewData.isFieldHighlighted?.(
                                'details.five.id'
                              ) ||
                              !!previewData.isFieldHighlighted?.(
                                'details.five.label'
                              ) ||
                              !!previewData.isFieldHighlighted?.(
                                'details.five.defaultValue'
                              )
                            }
                            mtWhenNoLabel="15px"
                            textAlign={detailsFiveAlignment}
                          />
                        </Box>
                      )}

                      {/* DETAILS SIX */}
                      {(!!previewData.detailsSixId ||
                        !!previewData.detailsSixLabel ||
                        !!previewData.detailsSixValue ||
                        !!previewData.isFieldHighlighted?.('details.six.id') ||
                        !!previewData.isFieldHighlighted?.(
                          'details.six.label'
                        ) ||
                        !!previewData.isFieldHighlighted?.(
                          'details.six.defaultValue'
                        )) && (
                        <Box
                          flex="1"
                          minW="50px"
                          maxW="100px"
                          overflow="hidden"
                          pl={2}
                          display="flex"
                          justifyContent={mapAlignToJustify(
                            detailsSixAlignment
                          )}
                        >
                          <GooglePassField
                            hideIdPlaceholder={previewData.hideIdPlaceholder}
                            label={previewData.detailsSixLabel || ''}
                            value={previewData.detailsSixValue}
                            id={previewData.detailsSixId}
                            color={getContrastColor}
                            isHighlighted={
                              !!previewData.isFieldHighlighted?.(
                                'details.six.id'
                              ) ||
                              !!previewData.isFieldHighlighted?.(
                                'details.six.label'
                              ) ||
                              !!previewData.isFieldHighlighted?.(
                                'details.six.defaultValue'
                              )
                            }
                            mtWhenNoLabel="15px"
                            textAlign={detailsSixAlignment}
                          />
                        </Box>
                      )}
                    </>
                  )}
              </Flex>
            </>
          )}

          {/* BARCODE */}
          {/* {(createView || clientView || isEdit || createTemplate) && */}
          {createTemplate &&
            previewBarcodeImage &&
            !!previewData.barcode.enabled &&
            !!previewData.barcode.type &&
            !previewData.googleNfcEnabled && (
              <>
                <Flex mt={2} w="full" justifyContent="center">
                  <Flex
                    bg="white"
                    mt={2}
                    py={2}
                    px={1}
                    borderRadius="10px"
                    w="fit-content"
                    justifyContent="center"
                    alignItems="center"
                    border={
                      isBarcodeFieldHighlighted ? '2px solid #FF4935' : 'none'
                    }
                    p={isBarcodeFieldHighlighted ? '4px' : '1'}
                  >
                    <BarcodePreview
                      type={previewData.barcode.type}
                      value={previewData.barcode.value}
                    />
                  </Flex>
                </Flex>

                {/* BARCODE DISPLAY TEXT — always shown on Google (Google bug workaround) */}
                {(previewData.barcode.source !== 'custom' ||
                  (previewData.barcode.source === 'custom' &&
                    previewData.barcode.value)) && (
                  <>
                    <Flex w="full" justifyContent="center">
                      <Text
                        fontSize="13px"
                        textStyle="headingFamilyRegularApplePay"
                        my={2}
                        color={getContrastColor}
                        borderRadius="4px"
                        border="none"
                        p="1"
                      >
                        {previewData.barcode.value !== ''
                          ? previewData.barcode.value
                          : previewData.barcode.source === 'custom'
                            ? ''
                            : previewData.barcode.source?.toUpperCase()}
                      </Text>
                    </Flex>
                  </>
                )}
              </>
            )}

          {/* BANNER */}
          {shouldShowBanner ? (
            <Box
              w="full"
              bg={previewData.passBackgroundColor}
              mt="15px"
              borderBottomLeftRadius="25px"
              borderBottomRightRadius="25px"
              position="relative"
              onMouseEnter={() => setIsBannerHovered(true)}
              onMouseLeave={() => setIsBannerHovered(false)}
            >
              <Image
                borderBottomLeftRadius="25px"
                borderBottomRightRadius="25px"
                w="full"
                h="auto"
                objectFit="cover"
                src={
                  !!previewData.passDesign &&
                  !!previewData.passDesign.bannerImageUrl &&
                  previewData.passType !== 'generic' &&
                  previewData.passType !== 'transit'
                    ? previewData.passDesign.bannerImageUrl
                    : previewData.googleBannerImage
                      ? previewData.googleBannerImage
                      : previewData.bannerImage
                }
                alt=""
                aspectRatio={googleBannerAspectRatio}
                boxShadow={
                  previewData.isFieldHighlighted?.('design.shared.banner') ||
                  previewData.isFieldHighlighted?.('design.google.banner') ||
                  previewData.isStampBannerHighlighted
                    ? '0 0 0 2px #FF4935'
                    : 'none'
                }
              />

              {!isBannerHovered && <TrialBannerOverlay isInTrial={isInTrial} />}
              {/* WATERMARK */}
              {createTemplate &&
                watermarkEnabled &&
                (!previewData.googleBannerImage
                  ? previewData.displayBannerWatermarkPreview
                  : previewData.displayGoogleBannerWatermarkPreview) && (
                  <Box
                    position="absolute"
                    top={
                      (watermarkPosition === 'topLeft' ||
                        watermarkPosition === 'topRight') &&
                      '0'
                    }
                    bottom={
                      (watermarkPosition === 'bottomLeft' ||
                        watermarkPosition === 'bottomRight') &&
                      '0'
                    }
                    left={
                      (watermarkPosition === 'topLeft' ||
                        watermarkPosition === 'bottomLeft') &&
                      '0'
                    }
                    right={
                      (watermarkPosition === 'topRight' ||
                        watermarkPosition === 'bottomRight') &&
                      '0'
                    }
                    mx="9px"
                    my="5px"
                    border={
                      isWatermarkFieldHighlighted ? '2px solid #FF4935' : 'none'
                    }
                    p={isWatermarkFieldHighlighted ? '4px' : '0'}
                  >
                    <Image
                      src={
                        watermarkColor === 'light'
                          ? previewWatermarkLight
                          : previewWatermarkDark
                      }
                      alt="watermark"
                      w="112px"
                      h="18.5px"
                    />
                  </Box>
                )}
            </Box>
          ) : previewData.passType !== 'transit' ? (
            <ImagePlaceholder
              h={previewData.passType !== 'eventTicket' ? '100%' : '90px'}
              w="full"
              mt="15px"
              aspectRatio={googleBannerAspectRatio}
              borderBottomLeftRadius="25px"
              borderBottomRightRadius="25px"
              boxShadow={
                previewData.isFieldHighlighted?.('design.shared.banner') ||
                previewData.isFieldHighlighted?.('design.google.banner')
                  ? '0 0 0 2px #FF4935'
                  : 'none'
              }
            />
          ) : (
            <Box h="20px" />
          )}
        </Box>

        {/* SHOW CODE */}
        {previewData.googleNfcEnabled && previewData.barcode.enabled && (
          <Flex justifyContent="center" alignItems="center" mt={3}>
            <Text fontSize="12px" textStyle="headingFamilyRegularGooglePay">
              Show code
            </Text>
          </Flex>
        )}
      </Box>

      {/* APP LINK */}
      {hasAppLink && (
        <Flex
          position="absolute"
          inset="85% 10% auto 0"
          width="full"
          justifyContent="center"
          direction="row"
          align="center"
          zIndex={2}
        >
          <Button
            w="75%"
            size="sm"
            variant="none"
            borderRadius="25px"
            border="1px solid"
            borderColor="black"
            _hover={{ bg: '#EFEFEF' }}
            mx="10px"
          >
            <Link
              href={getGoogleAppLinkHref(previewData)}
              isExternal
              textDecoration="none"
              _hover={{ textDecoration: 'none' }}
            >
              <Flex direction="row" alignItems="center" gap={2} color="#1D60D3">
                <CustomLinkIcon boxSize="11px" />
                <Text fontSize="12px" textStyle="headingFamilyRegularGooglePay">
                  {previewData.googleAppLinkDisplayText ||
                    `Open issuer's ${
                      previewData.googleAppLinkPlayStoreUrl ||
                      previewData.googleAppLinkPackageName
                        ? 'app'
                        : 'website'
                    }`}
                </Text>
              </Flex>
            </Link>
          </Button>
        </Flex>
      )}

      {/* BAR ICON */}
      {!templatesPage && (
        <Flex
          position="absolute"
          inset="91% 10% auto 0"
          my={3}
          width="full"
          justifyContent="center"
          direction="row"
          align="center"
        >
          <Divider
            orientation="vertical"
            borderColor="#656565"
            w="25%"
            borderWidth="2px"
            borderRadius="25px"
          />
        </Flex>
      )}
    </>
  );
};

export default GooglePassFront;
